// Warehouse pipeline policy — who may HOLD Packaging House material, and
// whether privileged roles may draw straight off the warehouse shelf.
//
// Umer's bug report (Sep 2 2026): an Admin / Super Admin opening the
// Packaging House could pick ANY lot still on the warehouse shelf — material
// the Warehouse Manager never sent — and make a batch from it, skipping the
// hand-off entirely. Staff never could; only the "direct mode" roles.
//
// Rule now: the Packaging House only ever works from material that was SENT
// to the person at the screen, whatever their role. Anyone who can open the
// Packaging House can be a recipient of "Send to Packaging House" — including
// the sender themselves ("Me") — which is how an Admin / Super Admin / WM gets
// material onto their own table.
//
// The old shelf-draw path (bulk_direct sources, the "All Material" view,
// consuming other people's assignments) is kept in the code behind this flag
// so it can be restored in one line if the client changes their mind — same
// convention as SHOW_MIX_INTO_NEW_MATERIAL in pages/WorkerHub.tsx.
import { UserRole } from '../types';

export const PACKAGING_HOUSE_SHELF_DRAW = false;

/** Roles that can open the Packaging House and therefore receive/hold material. */
export const PACKAGING_HOUSE_ROLES: readonly UserRole[] = [
    UserRole.WORKER,
    UserRole.PACKAGER, // legacy packager accounts — treated as staff
    UserRole.WAREHOUSE_MANAGER,
    UserRole.MANAGER,
    UserRole.ADMIN,
    UserRole.OWNER, UserRole.SUPER_ADMIN,
];

/** Packaging House staff proper — the people the WM normally hands material to. */
export const isPackagingStaffRole = (role: UserRole | undefined | null): boolean =>
    role === UserRole.WORKER || role === UserRole.PACKAGER;

export const canReceivePackagingMaterial = (role: UserRole | undefined | null): boolean =>
    !!role && PACKAGING_HOUSE_ROLES.includes(role);

// ─── Direct assignment from the warehouse (Sep 2026 ticket) ─────────────
// "Send to Store / Driver" on the Warehouse page: packs are built from a lot
// on the shelf and sent straight to a store or driver, bypassing the
// Packaging House hand-off. This is the WAREHOUSE MANAGER drawing off their
// OWN shelf — it is not the parked shelf-draw above (a Packaging House user
// pulling material nobody sent them), which stays off.
//
// Mirrors is_wm_tier() in Postgres (migrations 0047 + 0056): the
// warehouse_direct_assign function refuses every other role server-side, so
// this list only decides who SEES the option.
export const DIRECT_ASSIGN_ROLES: readonly UserRole[] = [
    UserRole.WAREHOUSE_MANAGER,
    UserRole.MANAGER,
    UserRole.ADMIN,
    UserRole.OWNER, UserRole.SUPER_ADMIN,
];

export const canAssignDirectFromWarehouse = (role: UserRole | undefined | null): boolean =>
    !!role && DIRECT_ASSIGN_ROLES.includes(role);
